import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'

/**
 * User input nodes (camera-in-blueprint). These are the graph's window onto viewport
 * pointer/keyboard input, owned by the `inputComponent` singleton (src/lib/inputComponent.js)
 * and dispatched through EventRuntime.fireEvent like every other event.
 *
 * Dispatch cadence, so the pin semantics aren't surprising:
 *   - onPointerDown/Up, onKeyDown/Up fire IMMEDIATELY on the DOM event.
 *   - onPointerDrag / onWheel fire ONCE PER FRAME with deltas coalesced since the last
 *     frame, so out-dx/out-dy are "motion this frame", not "motion since the last DOM event".
 *
 * Bound nodes (a key binding, a button filter) only run for matching payloads — see
 * eventStarters.js. `Is Held` / `Get Input Axis` are DATA nodes: hold is something the graph
 * ASKS about, never something it gets told 60×/s.
 */

const IN_ACCENT = '#ff7040'   // orange — events, matching InputKeyNode
const IN_HEADER = '#2a0f06'
const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
const dataPin = { background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }
const boolPin = { background: '#d06060', width: 10, height: 10, borderRadius: '50%', border: '2px solid #d0606044' }
const stop = e => e.stopPropagation()

const selectStyle = {
  width: '100%', boxSizing: 'border-box', background: '#160805', border: '1px solid #4a1c08',
  color: '#e0956a', padding: '3px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
}

function EventShell({ selected, fired, icon, label, tip, minWidth = 200, children }) {
  return (
    <div title={tip} style={{
      background: selected ? '#200e08' : '#140808',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#5a2008') : '#5a2008'}`,
      borderRadius: 6, minWidth, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: IN_HEADER, padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: IN_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
        <div style={{ flex: 1 }} />
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>
      {children}
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="nodrag nowheel"
      style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
      onPointerDown={stop} onMouseDown={stop} onClick={stop}>
      <span style={{ color: '#8a5030', fontSize: 10, minWidth: 48 }}>{label}</span>
      {children}
    </div>
  )
}

/** A data OUT pin, right-aligned with its field name. */
function OutPin({ id, label, style = dataPin }) {
  return (
    <div style={{ padding: '3px 12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, position: 'relative' }}>
      <span style={{ color: '#8a5030', fontSize: 10 }}>{label}</span>
      <Handle type="source" position={Position.Right} id={id} style={style} />
    </div>
  )
}

function ButtonField({ value, onChange }) {
  return (
    <Row label="Button">
      <select className="nodrag" value={value ?? 'any'} onMouseDown={stop}
        onChange={e => onChange('button', e.target.value)} style={selectStyle}>
        {['any', 'left', 'middle', 'right'].map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </Row>
  )
}

/** Click-to-listen key capture. Same interaction as the legacy InputKeyNode. */
function KeyField({ value, onChange }) {
  const [listening, setListening] = useState(false)
  useEffect(() => {
    if (!listening) return
    const handler = (e) => {
      e.preventDefault()
      e.stopPropagation()
      onChange('key', e.key === ' ' ? 'Space' : e.key)
      setListening(false)
    }
    window.addEventListener('keydown', handler, { once: true, capture: true })
    return () => window.removeEventListener('keydown', handler, { capture: true })
  }, [listening, onChange])

  return (
    <Row label="Key">
      <button className="nodrag" onMouseDown={stop} onClick={() => setListening(l => !l)}
        style={{
          ...selectStyle, cursor: 'pointer', textAlign: 'center',
          borderColor: listening ? '#ff7040' : '#4a1c08',
          color: listening ? '#ffcc44' : '#e0956a',
        }}>
        {listening ? 'press a key…' : (value || '—')}
      </button>
    </Row>
  )
}

// ── Pointer events ───────────────────────────────────────────────────────────
export function OnPointerDownNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="🖱️" label="On Pointer Down"
      tip="Fires when a mouse button (or touch) goes down over the viewport. Filter by button, or 'any'.">
      <ButtonField value={data.button} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-button" label="button" />
      <OutPin id="out-x" label="x" />
      <OutPin id="out-y" label="y" />
    </EventShell>
  )
}

export function OnPointerUpNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="🖱️" label="On Pointer Up"
      tip="Fires when a mouse button is released. Also fires with canceled=true when the browser takes the pointer away (touch scroll takeover), so a latch always gets its release.">
      <ButtonField value={data.button} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-button" label="button" />
      <OutPin id="out-canceled" label="canceled" style={boolPin} />
    </EventShell>
  )
}

export function OnPointerDragNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="↔️" label="On Pointer Drag" minWidth={220}
      tip="Fires ONCE PER FRAME while a button is held and the pointer moved. out-dx/out-dy are the motion coalesced over that frame (canvas pixels). Wire them into Camera Add Yaw / Add Pitch / Pan Target.">
      <ButtonField value={data.button} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-dx" label="dx" />
      <OutPin id="out-dy" label="dy" />
      <OutPin id="out-x" label="x" />
      <OutPin id="out-y" label="y" />
      <OutPin id="out-shift" label="shift" style={boolPin} />
    </EventShell>
  )
}

export function OnWheelNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="🎡" label="On Wheel"
      tip="Fires once per frame with the wheel motion coalesced over that frame, normalized to pixels across browsers (deltaMode lines/pages are converted). Wire out-delta into Camera Add Dolly.">
      <OutPin id="out-delta" label="delta" />
      <OutPin id="out-ctrl" label="ctrl" style={boolPin} />
    </EventShell>
  )
}

// ── Key events ───────────────────────────────────────────────────────────────
export function OnKeyDownNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="⌨️" label="On Key Down"
      tip="Fires when the bound key is pressed while the viewport is hovered or focused (so typing in a panel never drives the camera). OS auto-repeat does NOT re-fire — use Is Key Held for continuous motion.">
      <KeyField value={data.key} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-key" label="key" />
    </EventShell>
  )
}

export function OnKeyUpNode({ data, selected }) {
  return (
    <EventShell selected={selected} fired={isFired(data)} icon="⌨️" label="On Key Up"
      tip="Fires when the bound key is released. Always delivered for a key that was pressed over the viewport, even if focus moved to a text field first — so a down/up latch can't stick.">
      <KeyField value={data.key} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-key" label="key" />
    </EventShell>
  )
}

// ── Data nodes ───────────────────────────────────────────────────────────────
function DataShell({ selected, icon, label, tip, minWidth = 190, children }) {
  return (
    <div title={tip} style={{
      background: selected ? '#0d1a0d' : '#0a120a',
      border: `2px solid ${selected ? selectedBorder('#1e3a26') : '#1e3a26'}`,
      borderRadius: 6, minWidth, fontFamily: 'var(--m-font)',
    }}>
      <div style={{
        background: selected ? '#122a18' : '#0e1f12', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: '#7ad29a', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
      </div>
      {children}
    </div>
  )
}

export function IsButtonHeldNode({ data, selected }) {
  return (
    <DataShell selected={selected} icon="✊" label="Is Button Held"
      tip="True while the button is down. A data read — this is how you do 'hold' without the runtime re-firing a chain at you every frame.">
      <Row label="Button">
        <select className="nodrag" value={data.button ?? 'any'} onMouseDown={stop}
          onChange={e => data.onChange?.('button', e.target.value)} style={selectStyle}>
          {['any', 'left', 'middle', 'right'].map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <OutPin id="out-value" label="held" style={boolPin} />
    </DataShell>
  )
}

export function IsKeyHeldNode({ data, selected }) {
  return (
    <DataShell selected={selected} icon="✊" label="Is Key Held"
      tip="True while the bound key is down. Use with a per-frame driver for continuous motion (WASD-style) instead of relying on key repeat.">
      <KeyField value={data.key} onChange={(k, v) => data.onChange?.(k, v)} />
      <OutPin id="out-value" label="held" style={boolPin} />
    </DataShell>
  )
}

export function GetInputAxisNode({ data, selected }) {
  return (
    <DataShell selected={selected} icon="📐" label="Get Input Axis" minWidth={200}
      tip="Live input axis. mouseX/mouseY are this frame's pointer motion (they zero once the frame's events are dispatched); wheel is this frame's wheel delta; pointerX/pointerY are absolute canvas coords.">
      <Row label="Axis">
        <select className="nodrag" value={data.axis ?? 'mouseX'} onMouseDown={stop}
          onChange={e => data.onChange?.('axis', e.target.value)} style={selectStyle}>
          {['mouseX', 'mouseY', 'wheel', 'pointerX', 'pointerY'].map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <OutPin id="out-value" label="value" />
    </DataShell>
  )
}
